import { createTheme } from "@mui/material/styles";
import { rawColor, colors } from "../constants/colors";

export const forestGreenTheme = createTheme({
  name: "forestGreen",
  palette: {
    mode: "dark",
    primary: { main: rawColor.forestGreen.main },
    secondary: { main: rawColor.forestGreen.light },
    background: {
      default: rawColor.forestGreen.dark,
      paper: rawColor.forestGreen.paper,
    },
    text: {
      primary: rawColor.forestGreen.text.primary,
      secondary: rawColor.forestGreen.text.secondary,
    },
    action: {
      hover: rawColor.forestGreen.sidebar.hover,
    },
    error: { main: rawColor.status.error.dark },
    warning: { main: rawColor.status.warning },
    success: { main: rawColor.forestGreen.main },
    divider: rawColor.forestGreen.divider,
  },
  sidebar: {
    base: colors.sidebar.forestGreen.base,
    hover: colors.sidebar.forestGreen.hover,
    selected: colors.sidebar.forestGreen.selected,
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.sidebar.base,
          borderRight: `1px solid ${rawColor.forestGreen.divider}`,
          "& .MuiListItemIcon-root": {
            color: rawColor.forestGreen.text.secondary,
          },
          "& .MuiListItemText-root": {
            color: rawColor.forestGreen.text.primary,
          },
          "& .MuiListItemButton-root.Mui-selected": {
            backgroundColor: theme.sidebar.selected,
            "& .MuiListItemIcon-root": {
              color: rawColor.forestGreen.main,
            },
            "& .MuiListItemText-root": {
              color: rawColor.forestGreen.main,
            },
          },
          "& .MuiListItemButton-root:hover": {
            backgroundColor: theme.sidebar.hover,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: colors.table.forestGreen.cell,
          color: rawColor.forestGreen.text.primary,
        },
        head: {
          backgroundColor: colors.table.forestGreen.header,
          color: colors.table.forestGreen.headerText,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: rawColor.forestGreen.paper,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          color: rawColor.forestGreen.text.primary,
        },
        h6: {
          color: rawColor.forestGreen.text.primary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: rawColor.forestGreen.text.primary,
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: rawColor.forestGreen.text.secondary,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "currentColor",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: rawColor.forestGreen.text.secondary,
          "& .MuiInputBase-input": {
            color: rawColor.forestGreen.text.secondary,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: rawColor.forestGreen.text.secondary,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.forestGreen.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.forestGreen.text.secondary,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: rawColor.forestGreen.main,
          },
          "& .MuiOutlinedInput-input": {
            color: rawColor.forestGreen.text.secondary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: rawColor.forestGreen.text.secondary,
          "&.Mui-focused": {
            color: rawColor.forestGreen.main,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: rawColor.forestGreen.text.secondary,
        },
        select: {
          color: rawColor.forestGreen.text.secondary,
        },
        selectLabel: {
          color: rawColor.forestGreen.text.secondary,
        },
        selectIcon: {
          color: rawColor.forestGreen.text.secondary,
        },
        displayedRows: {
          color: rawColor.forestGreen.text.secondary,
        },
        actions: {
          color: rawColor.forestGreen.text.secondary,
          "& .MuiSvgIcon-root": {
            color: rawColor.forestGreen.text.secondary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-option": {
            color: rawColor.forestGreen.text.secondary,
            "&:hover": {
              backgroundColor: rawColor.forestGreen.sidebar.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: rawColor.forestGreen.sidebar.selected,
              color: rawColor.forestGreen.main,
              "&:hover": {
                backgroundColor: rawColor.forestGreen.sidebar.selected,
              },
            },
          },
          "& .MuiAutocomplete-listbox": {
            backgroundColor: rawColor.forestGreen.paper,
            "& .MuiAutocomplete-option": {
              padding: "8px 16px",
            },
          },
          "& .MuiAutocomplete-option .MuiTypography-root": {
            color: "inherit",
          },
          "& .MuiAutocomplete-option .MuiListItemText-root": {
            color: "inherit",
          },
        },
        paper: {
          backgroundColor: rawColor.forestGreen.paper,
          "& .MuiAutocomplete-option": {
            color: rawColor.forestGreen.text.secondary,
          },
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            color: rawColor.forestGreen.text.secondary,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        ".select__control": {
          backgroundColor: `${rawColor.forestGreen.paper} !important`,
          borderColor: `${rawColor.forestGreen.divider} !important`,
          "&:hover": {
            borderColor: `${rawColor.forestGreen.text.secondary} !important`,
          },
          "&--is-focused": {
            borderColor: `${rawColor.forestGreen.main} !important`,
            boxShadow: `0 0 0 1px ${rawColor.forestGreen.main} !important`,
          },
        },
        ".select__menu": {
          backgroundColor: `${rawColor.forestGreen.paper} !important`,
          border: `1px solid ${rawColor.forestGreen.divider} !important`,
        },
        ".select__option": {
          backgroundColor: `${rawColor.forestGreen.paper} !important`,
          color: `${rawColor.forestGreen.text.secondary} !important`,
          "&:hover": {
            backgroundColor: `${rawColor.forestGreen.sidebar.hover} !important`,
          },
          "&--is-focused": {
            backgroundColor: `${rawColor.forestGreen.sidebar.hover} !important`,
          },
          "&--is-selected": {
            backgroundColor: `${rawColor.forestGreen.sidebar.selected} !important`,
            color: `${rawColor.forestGreen.main} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.forestGreen.sidebar.selected} !important`,
            },
          },
        },
        ".select__multi-value": {
          backgroundColor: `${rawColor.forestGreen.sidebar.hover} !important`,
          "&__label": {
            color: `${rawColor.forestGreen.text.secondary} !important`,
          },
          "&__remove": {
            color: `${rawColor.forestGreen.text.secondary} !important`,
            "&:hover": {
              backgroundColor: `${rawColor.forestGreen.sidebar.selected} !important`,
              color: `${rawColor.forestGreen.main} !important`,
            },
          },
        },
        ".select__placeholder": {
          color: `${rawColor.forestGreen.text.secondary} !important`,
        },
        ".select__single-value": {
          color: `${rawColor.forestGreen.text.secondary} !important`,
        },
        ".select__input": {
          color: `${rawColor.forestGreen.text.secondary} !important`,
        },
        ".select__indicator": {
          color: `${rawColor.forestGreen.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.forestGreen.main} !important`,
          },
        },
        ".select__indicator-separator": {
          backgroundColor: `${rawColor.forestGreen.divider} !important`,
        },
        ".select__clear-indicator": {
          color: `${rawColor.forestGreen.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.forestGreen.main} !important`,
          },
        },
        ".select__dropdown-indicator": {
          color: `${rawColor.forestGreen.text.secondary} !important`,
          "&:hover": {
            color: `${rawColor.forestGreen.main} !important`,
          },
        },
      },
    },
  },
});
